import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { ApiError, api, type PlatformUserRecord, type SetupLink } from '../lib/api';
import { Empty, ErrorNote, PageHeading, SetupLinkPanel, Spinner } from '../components/ui';
import { Confirm } from '../components/Confirm';
import {
  Ledger,
  LedgerToolbar,
  onStage,
  statusFacet,
  type LedgerColumn,
  type LedgerGroup,
} from '../components/Ledger';
import { useListView } from '../lib/useListView';

const ROLE_LABEL: Record<PlatformUserRecord['role'], string> = {
  super_admin: 'Platform admin',
  org_admin: 'Administrator',
  teacher: 'Teacher',
};

const PLATFORM = 'platform';

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function columnsFor(actions: {
  /** Both ask first and wait on the promise; Reactivate alone is one click. */
  onLink: (user: PlatformUserRecord) => Promise<unknown>;
  onToggle: (user: PlatformUserRecord) => Promise<unknown>;
  linking: boolean;
  toggling: boolean;
}): LedgerColumn<PlatformUserRecord>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      sort: (user) => user.name,
      className: 'font-medium',
      cell: (user) => (
        <span className={user.isActive ? '' : 'text-ink-faint line-through'}>{user.name}</span>
      ),
    },
    {
      id: 'identifier',
      header: 'Signs in with',
      sort: (user) => user.email ?? user.username,
      className: 'font-mono text-xs text-ink-soft',
      cell: (user) => user.email ?? user.username ?? '-',
    },
    {
      id: 'role',
      header: 'Role',
      sort: (user) => ROLE_LABEL[user.role],
      cell: (user) => ROLE_LABEL[user.role],
    },
    {
      id: 'seen',
      header: 'Last signed in',
      sort: (user) => user.lastLoginAt,
      className: 'font-mono text-xs text-ink-soft',
      cell: (user) =>
        user.pendingSetup
          ? 'setup pending'
          : user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleDateString()
            : 'never',
    },
    {
      id: 'actions',
      header: 'Actions',
      headerHidden: true,
      align: 'right',
      cell: (user, ctx) => (
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="btn"
            aria-expanded={ctx.expanded}
            aria-controls={ctx.detailId ?? undefined}
            onClick={ctx.toggle}
          >
            {ctx.expanded ? 'Close' : 'Edit'}
          </button>
          <Confirm
            trigger="Setup link"
            title={`Send ${user.name} a new setup link?`}
            description="Any setup link already issued to them stops working. Their current password keeps working until they use the new one."
            confirmLabel="Setup link"
            pendingLabel="Creating…"
            disabled={!user.isActive || actions.linking}
            onConfirm={() => actions.onLink(user)}
            errorFallback="Could not create a setup link."
          />
          {user.isActive ? (
            <Confirm
              trigger="Deactivate"
              title={`Deactivate ${user.name}?`}
              description="They are signed out everywhere and can no longer sign in. Anything they marked stays attributed to them."
              confirmLabel="Deactivate"
              pendingLabel="Deactivating…"
              disabled={actions.toggling}
              onConfirm={() => actions.onToggle(user)}
              errorFallback="Could not deactivate that account."
            />
          ) : (
            <button
              type="button"
              className="btn"
              disabled={actions.toggling}
              onClick={() => void actions.onToggle(user).catch(() => {})}
            >
              Reactivate
            </button>
          )}
        </div>
      ),
    },
  ];
}

/*
 * By organization, and with no declared order, because the server already
 * answers that question: it sorts platform accounts first and then by
 * organization name, with a comment saying why. Rendering its order as visible
 * groups makes that sentence true on the screen instead of only in the SQL, and
 * the Organization column goes away because every row in a group repeats it.
 */
const BY_ORG: LedgerGroup<PlatformUserRecord> = {
  of: (user) => user.organizationId ?? PLATFORM,
  label: (id, within) => (
    <>
      {id === PLATFORM ? 'Platform' : (within[0]?.organizationName ?? 'Organization')}
      <span className="font-mono text-xs font-normal text-ink-soft tabular">{within.length}</span>
    </>
  ),
};

const editUser = (user: PlatformUserRecord, close: () => void) => (
  <EditUser user={user} onDone={close} />
);

/**
 * Every account on the deployment, in one table.
 *
 * This exists apart from the per-organization Staff page because a platform
 * administrator belongs to no organization, and the org-scoped routes cannot
 * see such a row at all - row-level security compares organization_id to the
 * current organization, and NULL never matches.
 */
export function PlatformPeople() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [link, setLink] = useState<SetupLink | null>(null);
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set());

  const orgs = useQuery({ queryKey: ['platform-orgs'], queryFn: api.platform.organizations });

  const search = useSearch({ from: '/app/platform/people' });
  const scope = search.org ?? '';

  /*
   * The one search in this app that runs on the server: this list spans every
   * tenant, so it is the only one that cannot be narrowed from what is already
   * loaded.
   *
   * It is debounced without a timer of its own. The box runs ahead of the
   * address, the address is written a quarter second after the typing stops,
   * and the key below reads the address, so the request waits for the pause as
   * a consequence of the view being addressable. Before this, every keystroke
   * minted a request and a cache entry.
   *
   * keepPreviousData holds the rows on screen while the next answer arrives.
   * Without it the table empties and refills on each search, and the live
   * region announces its way down to nothing and back.
   */
  const users = useQuery({
    queryKey: ['platform-users', search.q ?? '', scope],
    queryFn: () =>
      api.platform.users({
        ...(search.q ? { q: search.q } : {}),
        ...(scope ? { organizationId: scope } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['platform-users'] });

  const sendLink = useMutation({
    mutationFn: (id: string) => api.platform.setupLink(id),
    onSuccess: async (data) => {
      setLink(data);
      await invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.platform.updateUser(id, { isActive }),
    onSuccess: invalidate,
  });

  const columns = useMemo(
    () =>
      columnsFor({
        onLink: (user) => sendLink.mutateAsync(user.id),
        onToggle: (user) => toggle.mutateAsync({ id: user.id, isActive: !user.isActive }),
        linking: sendLink.isPending,
        toggling: toggle.isPending,
      }),
    [sendLink, toggle],
  );
  const view = useListView({ search, columns });
  const show = view.get('show');

  /*
   * The narrowing here is client side even though the search beside it is not.
   * isActive is on every row already, so asking the server again would be a
   * round trip to drop rows this page is holding.
   */
  const all = useMemo(() => users.data?.users ?? [], [users.data]);
  const rows = useMemo(() => onStage(all, show), [all, show]);
  const organizations = orgs.data?.organizations ?? [];

  return (
    <div>
      <PageHeading eyebrow="Every account on this deployment" title="People">
        <button type="button" className="btn btn-primary" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add person'}
        </button>
      </PageHeading>

      <p className="mb-5 max-w-2xl text-sm text-ink-soft">
        Editing anyone here acts outside every organization, so it is written to the audit log of
        the organization the account belongs to.
      </p>

      {link ? (
        <SetupLinkPanel url={link.url} expiresAt={link.expiresAt} onDismiss={() => setLink(null)} />
      ) : null}

      {sendLink.error ? (
        <div className="mb-5">
          <ErrorNote message={message(sendLink.error, 'Could not generate a setup link.')} />
        </div>
      ) : null}
      {toggle.error ? (
        <div className="mb-5">
          <ErrorNote message={message(toggle.error, 'Could not update that account.')} />
        </div>
      ) : null}

      {adding ? (
        <CreateForm
          organizations={organizations}
          onDone={async (setup) => {
            setAdding(false);
            if (setup) setLink(setup);
            await invalidate();
          }}
        />
      ) : null}

      {users.isLoading ? (
        <Spinner />
      ) : (
        <>
          <LedgerToolbar
            facets={[
              statusFacet({
                /*
                 * Two chips. A platform account carries no organization_id, so
                 * the policy that hides deleted rows never reaches it and there
                 * is nothing a third chip could show.
                 */
                label: 'Filter people by status',
                liveLabel: 'Active',
                inactiveLabel: 'Deactivated',
                value: show,
                onChange: (next) => view.set({ show: next || undefined }),
                live: onStage(all, '').length,
                inactive: onStage(all, 'inactive').length,
              }),
            ]}
            query={view.draft}
            onQuery={view.onQuery}
            searchPlaceholder="name, email or username"
            shown={rows.length}
            total={all.length}
          >
            <label className="grid gap-1.5">
              <span className="eyebrow">Organization</span>
              <select
                className="field"
                value={scope}
                onChange={(e) => view.set({ org: e.target.value || undefined })}
              >
                <option value="">All</option>
                <option value={PLATFORM}>Platform accounts</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </label>
          </LedgerToolbar>
          <Ledger
            caption="People"
            rows={rows}
            total={all.length}
            columns={columns}
            rowId={(user) => user.id}
            sort={view.sort}
            onSort={view.onSort}
            /*
             * Unconditional: organization and whether the account works are
             * different questions, so the filter takes rows out of these
             * headings rather than removing the headings.
             */
            group={BY_ORG}
            collapsed={shut}
            onCollapse={(id, closed) =>
              setShut((prev) => {
                const next = new Set(prev);
                if (closed) next.add(id);
                else next.delete(id);
                return next;
              })
            }
            expanded={editing}
            onExpand={setEditing}
            detail={editUser}
            empty={
              <Empty
                title="Nobody here"
                hint={
                  view.query || scope
                    ? 'No account matches that search.'
                    : 'Accounts appear here as organizations are created.'
                }
              />
            }
            noMatch={
              <Empty
                title="Nobody to show"
                hint="Every account here is deactivated. Switch to All to see them."
              />
            }
          />
        </>
      )}
    </div>
  );
}

/**
 * Creating an account anywhere. Choosing "Platform account" switches the
 * identity fields, because the two shapes are genuinely different: an
 * organization member signs in with an email, a platform operator may have only
 * a username and no mailbox at all.
 */
function CreateForm({
  organizations,
  onDone,
}: {
  organizations: { id: string; name: string }[];
  onDone: (setup: SetupLink | null) => Promise<void>;
}) {
  const [organizationId, setOrganizationId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<'org_admin' | 'teacher'>('teacher');
  const [withPassword, setWithPassword] = useState(false);
  const [password, setPassword] = useState('');

  const isPlatform = organizationId === PLATFORM;

  const create = useMutation({
    mutationFn: () =>
      api.platform.createUser({
        organizationId: isPlatform ? null : organizationId,
        name,
        role: isPlatform ? 'super_admin' : role,
        ...(email ? { email } : {}),
        ...(isPlatform && username ? { username } : {}),
        ...(withPassword ? { password } : {}),
      }),
    onSuccess: (created) => onDone(created.setup),
  });

  return (
    <form
      className="panel mb-5 grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      {create.error ? (
        <div className="sm:col-span-2 lg:col-span-3">
          <ErrorNote message={message(create.error, 'Could not create that account.')} />
        </div>
      ) : null}

      <label className="grid gap-1.5">
        <span className="eyebrow">Belongs to</span>
        <select
          className="field"
          required
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
        >
          <option value="">Choose…</option>
          <option value={PLATFORM}>Platform account - no organization</option>
          {organizations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name}
            </option>
          ))}
        </select>
      </label>

      <label className="grid gap-1.5">
        <span className="eyebrow">Name</span>
        <input className="field" required value={name} onChange={(e) => setName(e.target.value)} />
      </label>

      <label className="grid gap-1.5">
        <span className="eyebrow">Email{isPlatform ? ' (optional)' : ''}</span>
        <input
          className="field"
          type="email"
          required={!isPlatform}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>

      {isPlatform ? (
        <label className="grid gap-1.5">
          <span className="eyebrow">Username</span>
          <input
            className="field font-mono"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="an operator may have no mailbox"
          />
        </label>
      ) : (
        <label className="grid gap-1.5">
          <span className="eyebrow">Role</span>
          <select
            className="field"
            value={role}
            onChange={(e) => setRole(e.target.value as 'org_admin' | 'teacher')}
          >
            <option value="teacher">Teacher - only assigned classes</option>
            <option value="org_admin">Administrator - everything in the organization</option>
          </select>
        </label>
      )}

      <fieldset className="grid gap-1.5 sm:col-span-2 lg:col-span-3">
        <legend className="eyebrow mb-1.5">How they get in</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="platform-credential"
            checked={!withPassword}
            onChange={() => setWithPassword(false)}
          />
          Send a setup link - they choose their own password
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="platform-credential"
            checked={withPassword}
            onChange={() => setWithPassword(true)}
          />
          Set a password now
        </label>
        {withPassword ? (
          <input
            className="field mt-1.5 font-mono sm:max-w-sm"
            type="text"
            required
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="at least 12 characters"
          />
        ) : null}
      </fieldset>

      <div className="sm:col-span-2 lg:col-span-3">
        <button className="btn btn-primary" type="submit" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create account'}
        </button>
      </div>
    </form>
  );
}

/**
 * Editing an account, under the row rather than over it.
 *
 * The password field used to sit in the "Last signed in" column, which is not
 * a sensible thing for a table cell to hold. Down here the form gets the full
 * width and can say what each field is.
 */
function EditUser({ user, onDone }: { user: PlatformUserRecord; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email ?? '');
  const [username, setUsername] = useState(user.username ?? '');
  const [role, setRole] = useState(user.role);
  const [password, setPassword] = useState('');

  const isPlatform = user.organizationId === null;

  const save = useMutation({
    mutationFn: () =>
      api.platform.updateUser(user.id, {
        name,
        email: email ? email : null,
        ...(isPlatform ? { username: username ? username : null } : {}),
        ...(isPlatform ? {} : { role }),
        ...(password ? { password } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      onDone();
    },
  });

  return (
    <form
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {save.error ? (
        <div className="sm:col-span-2 lg:col-span-3">
          <ErrorNote message={message(save.error, 'Could not save.')} />
        </div>
      ) : null}
      <label className="grid gap-1.5">
        <span className="eyebrow">Name</span>
        <input className="field" required value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="grid gap-1.5">
        <span className="eyebrow">Email</span>
        <input
          className="field font-mono text-xs"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      {isPlatform ? (
        <label className="grid gap-1.5">
          <span className="eyebrow">Username</span>
          <input
            className="field font-mono text-xs"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>
      ) : (
        <label className="grid gap-1.5">
          <span className="eyebrow">Role</span>
          <select
            className="field"
            value={role}
            onChange={(e) => setRole(e.target.value as PlatformUserRecord['role'])}
          >
            <option value="teacher">Teacher</option>
            <option value="org_admin">Administrator</option>
          </select>
        </label>
      )}
      <label className="grid gap-1.5">
        <span className="eyebrow">New password</span>
        <input
          className="field font-mono text-xs"
          type="text"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="leave blank to keep the current one"
        />
      </label>
      <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
        <div className="ml-auto flex gap-2">
          <button type="button" className="btn" onClick={onDone}>
            Cancel
          </button>
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </form>
  );
}
